/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Resolve,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { IAccountData } from '@app/models/account-data.model';
import { Pages } from '@app/models/Pages';
import { IUserAccMarketPlace } from '@app/models/user-info.model';
import { NGXLogger } from 'ngx-logger';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { AccountService } from './account.service';
import { FiProductsService } from './fi-products.service';
import { LoggingService } from './logging.service';
import { ProposalService } from './proposal.service';
import { UnityDirectStateService } from './unity-direct-state.service';
import { UserInfoService } from './user-info.service';
import { VinLookupService } from './vin-lookup.service';

@Injectable({ providedIn: 'root' })
export class UnityDirectResolver implements Resolve<any> {
  constructor(
    @Inject(UnityDirectStateService) private readonly udStateService,
    @Inject(UserInfoService) private readonly userInfoService,
    @Inject(AccountService) private readonly accountService,
    @Inject(ProposalService) private readonly proposalService,
    @Inject(FiProductsService) private readonly fiProductsService,
    private readonly router: Router,
    private readonly ls: LoggingService,
    private readonly logger: NGXLogger,
    private readonly _vin: VinLookupService
  ) {}

  resolve(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<any> | any {
    const id: string = this.router.getCurrentNavigation().extras.state?.id;
    this.logger.info(
      'Fetching data for proposal with ID: ',
      id,
      " (if undefined it's a new proposal)"
    );
    this.udStateService.proposalId = id;
    this.udStateService.bundleId =
      this.router.getCurrentNavigation().extras.state?.bundleId;

    return this.userInfoService.getUserInfo().pipe(
      concatMap((userInfo: IUserAccMarketPlace) => {
        const userAccountIndex = this.userInfoService.getAccountIndex(userInfo);
        /* check if cookie set or not*/
        if (userAccountIndex >= 0) {
          this.udStateService.userInfo = userInfo[userAccountIndex];
          return this.accountService.getAccount(userInfo[userAccountIndex]);
        }
        this.udStateService.userInfo = userInfo[0];
        return this.accountService.getAccount(userInfo[0]);
      }),
      concatMap((accountInfo: IAccountData) => {
        this.udStateService.userAccount = accountInfo;
        return this.fiProductsService.getFiProducts(
          this.udStateService.userInfo
        );
      }),
      concatMap(() => {
        return this._vin.getOtherCollateralList(
          this.udStateService.userAccount.portalAccountId
        );
      }),
      concatMap(() => {
        if (id) {
          return combineLatest([
            this.proposalService.getRatesForProposal(id),
            this.fiProductsService.getFiProducts(this.udStateService.userInfo),
            this.proposalService.getProposal({ id }),
            this._vin.getOtherCollateralList(
              this.udStateService.userAccount.portalAccountId
            )
          ]).pipe(
            map(
              ([
                bundlesPageData,
                productsData,
                proposalData,
                otherCollateralList
              ]) => ({
                bundlesPageData,
                productsData,
                proposalData,
                otherCollateralList
              })
            )
          );
        } else {
          return this.fiProductsService.getFiProducts(
            this.udStateService.userInfo
          );
        }
      }),
      catchError(() => {
        this.ls.sendErrorLevelMessage('Unity Direct Resolver Error', this, {
          error: 'There was an error completing the unity direct resolver.'
        });
        return of(null);
      })
    );
  }
}
